import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { P, has } from "@majlis/protocol/permissions";
import type { Message, Channel } from "@majlis/protocol/types";

/** What the chat view needs from a channel — server text channels and DMs both fit. */
export type ChatChannel = Pick<Channel, "id" | "name" | "topic" | "permissions"> & { dm?: "dm" | "group_dm" };
import { del, patch, post, put, get, ApiError } from "../lib/api.ts";
import { t, timeOf, dayLabel } from "../lib/i18n.ts";
import { renderMarkdown, isJumbo } from "../lib/markdown.tsx";
import {
  useStore, getState, openChannel, loadOlder, sendMessage, retryPending, discardPending, upload, toast, errText,
  type PendingMessage, type ServerState, ack, setActiveChannel,
} from "../lib/store.ts";
import { Avatar, Icon, Confirm, EmojiPicker, QUICK_REACTIONS, displayName, userColor, Spinner, Modal } from "./common.tsx";

const MAX_UPLOAD = 25 * 1024 * 1024;
const GROUP_MS = 7 * 60_000;

// ---------- message rendering ----------
function Attachments({ m }: { m: Message }) {
  const [lightbox, setLightbox] = useState<string | null>(null);
  const media = m.attachments.filter((a) => a.contentType.startsWith("image/"));
  return (
    <>
      {media.length > 0 && (
        <div className={`media n${Math.min(media.length, 4)}`}>
          {media.map((a) => (
            <button key={a.id} className="imgbtn" onClick={() => setLightbox(a.url)} aria-label={t("openImage")}>
              <img src={a.url} alt={a.filename} loading="lazy" width={a.width ?? undefined} height={a.height ?? undefined} />
            </button>
          ))}
        </div>
      )}
      {m.attachments.filter((a) => !a.contentType.startsWith("image/")).map((a) =>
        a.contentType.startsWith("audio/") ? (
          <div key={a.id} className="voice"><Icon name="mic" size={18} /><audio controls preload="none" src={a.url} />
            {a.durationMs && <span className="mono">{Math.floor(a.durationMs / 60000)}:{String(Math.round(a.durationMs / 1000) % 60).padStart(2, "0")}</span>}</div>
        ) : a.contentType.startsWith("video/") ? (
          <video key={a.id} className="vid" controls preload="metadata" src={a.url} />
        ) : (
          <a key={a.id} className="fcard" href={a.url} download={a.filename}>
            <span className="fi">{(a.filename.split(".").pop() ?? "").slice(0, 4).toUpperCase() || <Icon name="file" />}</span>
            <span className="fm"><b dir="auto">{a.filename}</b><span className="mono">{a.size > 1e6 ? `${(a.size / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(a.size / 1024))} KB`}</span></span>
            <Icon name="download" size={18} />
          </a>
        ))}
      {lightbox && <div className="lightbox" onClick={() => setLightbox(null)} role="dialog" aria-label={t("openImage")}><img src={lightbox} alt="" /><button className="ib" aria-label={t("close")}><Icon name="x" /></button></div>}
    </>
  );
}

interface ItemProps { m: Message; grouped: boolean; channel: ChatChannel; server: ServerState | null; onReply: (m: Message) => void; onEdit: (m: Message) => void; onProfile: (userId: string) => void }

function MessageItem({ m, grouped, channel, server, onReply, onEdit, onProfile }: ItemProps) {
  const me = useStore((s) => s.me)!;
  const members = server?.members ?? null;
  const users = useStore((s) => s.users);
  const blocked = useStore((s) => s.relationships[m.author.id]?.type === "blocked");
  const [showBlocked, setShowBlocked] = useState(false);
  const [picker, setPicker] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const perms = BigInt(channel.permissions);
  const own = m.author.id === me.id;
  const canManage = has(perms, P.MANAGE_MESSAGES);
  const known = useMemo(() => new Set(members ? Object.values(members).map((x) => x.user.username) : Object.values(users).map((u) => u.username)), [members, users]);
  const mentionsMe = m.mentionEveryone || m.mentions.includes(me.id);
  const nick = members?.[m.author.id]?.nickname;
  const topRole = server && members?.[m.author.id] ? server.roles.filter((r) => members[m.author.id]!.roleIds.includes(r.id) && r.color).sort((a, b) => b.position - a.position)[0] : undefined;

  const react = async (emoji: string) => {
    setPicker(false);
    const mine = m.reactions.find((r) => r.emoji === emoji)?.me;
    try { await (mine ? del : put)(`/channels/${channel.id}/messages/${m.id}/reactions/${encodeURIComponent(emoji)}/@me`); } catch (e) { toast(errText(e)); }
  };
  return (
    <article className={`msg${grouped ? " cont" : ""}${mentionsMe ? " mentioned" : ""}`} id={`m-${m.id}`}>
      <div className="gut">{grouped ? <time className="hover-time mono">{timeOf(m.createdAt)}</time> : <button className="avbtn" onClick={() => onProfile(m.author.id)} aria-label={displayName(m.author)}><Avatar user={m.author} /></button>}</div>
      <div className="body">
        {m.replyTo && <a className="quote" href={`#m-${m.replyTo.id}`} onClick={(e) => { e.preventDefault(); document.getElementById(`m-${m.replyTo!.id}`)?.scrollIntoView({ block: "center", behavior: "smooth" }); }}>
          <b>{displayName(m.replyTo.author)}</b><span dir="auto">{m.replyTo.content || "📎"}</span></a>}
        {!grouped && <div className="hd">
          <button className="author" onClick={() => onProfile(m.author.id)} style={{ color: topRole?.color ? `#${topRole.color.toString(16).padStart(6, "0")}` : userColor(m.author.id) }} dir="auto">{nick || displayName(m.author)}</button>
          {server?.ownerId === m.author.id && <span className="tag">{t("owner")}</span>}
          <time className="mono" title={new Date(m.createdAt).toLocaleString()}>{timeOf(m.createdAt)}</time>
          {m.pinned && <Icon name="pin" size={13} className="pinned" />}
        </div>}
        {blocked && !showBlocked ? <button className="lnk small" onClick={() => setShowBlocked(true)}>{t("blockedMessage")}</button> : <>
        {m.content && <div className={`txt${isJumbo(m.content) ? " jumbo" : ""}`} dir="auto">
          {renderMarkdown(m.content, { meName: me.username, knownUsers: known })}{m.editedAt && <span className="edited"> {t("edited")}</span>}
        </div>}
        {m.attachments.length > 0 && <Attachments m={m} />}
        {m.reactions.length > 0 && <div className="reacts">
          {m.reactions.map((r) => <button key={r.emoji} className="re" aria-pressed={r.me} onClick={() => react(r.emoji)}>{r.emoji} <span className="mono">{r.count}</span></button>)}
        </div>}</>}
      </div>
      <div className="actbar" role="toolbar">
        {has(perms, P.ADD_REACTIONS) && QUICK_REACTIONS.slice(0, 3).map((e) => <button key={e} onClick={() => react(e)} aria-label={e}>{e}</button>)}
        {has(perms, P.ADD_REACTIONS) && <button onClick={() => setPicker(true)} title={t("react")} aria-label={t("react")}><Icon name="smile" size={17} /></button>}
        {has(perms, P.SEND_MESSAGES) && <button onClick={() => onReply(m)} title={t("reply")} aria-label={t("reply")}><Icon name="reply" size={17} /></button>}
        {own && m.content && <button onClick={() => onEdit(m)} title={t("edit")} aria-label={t("edit")}><Icon name="edit" size={17} /></button>}
        {canManage && <button onClick={async () => { try { await (m.pinned ? del : put)(`/channels/${channel.id}/pins/${m.id}`); } catch (e) { toast(errText(e)); } }} title={m.pinned ? t("unpin") : t("pin")} aria-label={m.pinned ? t("unpin") : t("pin")}><Icon name="pin" size={17} /></button>}
        {m.content && <button onClick={async () => { try { await navigator.clipboard.writeText(m.content); toast(t("copied")); } catch { toast(m.content); } }} title={t("copy")} aria-label={t("copy")}><Icon name="copy" size={17} /></button>}
        {(own || canManage) && <button className="danger" onClick={() => setConfirmDel(true)} title={t("delete")} aria-label={t("delete")}><Icon name="trash" size={17} /></button>}
        {picker && <EmojiPicker onPick={react} onClose={() => setPicker(false)} />}
      </div>
      {confirmDel && <Confirm title={t("delete")} body={t("deleteMessageQ")} confirmLabel={t("delete")} onClose={() => setConfirmDel(false)}
        onConfirm={() => del(`/channels/${channel.id}/messages/${m.id}`)} />}
    </article>
  );
}

function PendingItem({ p }: { p: PendingMessage }) {
  const me = useStore((s) => s.me)!;
  return (
    <article className={`msg pending ${p.status}`}>
      <div className="gut"><Avatar user={me} /></div>
      <div className="body">
        <div className="hd"><b dir="auto">{displayName(me)}</b><time className="mono">{p.status === "sending" ? t("sending") : t("failedToSend")}</time></div>
        {p.content && <div className="txt" dir="auto">{p.content}</div>}
        {p.attachments.map((a, i) => <div key={i} className="fcard"><span className="fi"><Icon name="file" /></span><span className="fm"><b dir="auto">{a.name}</b></span></div>)}
        {p.status === "failed" && <div className="row small"><span className="err">{p.error}</span>
          <button className="lnk" onClick={() => retryPending(p.channelId, p.nonce)}>{t("retry")}</button>
          <button className="lnk" onClick={() => discardPending(p.channelId, p.nonce)}>{t("delete")}</button></div>}
      </div>
    </article>
  );
}

// ---------- composer ----------
interface Upload { key: string; name: string; type: string; progress: number; att?: { id: string }; error?: string; abort?: () => void; preview?: string }

function Composer({ channel, replyTo, setReplyTo, editing, setEditing }:
  { channel: ChatChannel; replyTo: Message | null; setReplyTo: (m: Message | null) => void; editing: Message | null; setEditing: (m: Message | null) => void }) {
  const perms = BigInt(channel.permissions);
  const canSend = has(perms, P.SEND_MESSAGES);
  const canAttach = has(perms, P.ATTACH_FILES);
  const [text, setText] = useState("");
  const [files, setFiles] = useState<Upload[]>([]);
  const [picker, setPicker] = useState(false);
  const [rec, setRec] = useState<{ start: number; stop: (send: boolean) => void } | null>(null);
  const [recSecs, setRecSecs] = useState(0);
  const ta = useRef<HTMLTextAreaElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const lastTyping = useRef(0);
  const drafts = useRef<Record<string, string>>({});

  useEffect(() => { setText(drafts.current[channel.id] ?? ""); setFiles([]); ta.current?.focus(); }, [channel.id]);
  useEffect(() => { drafts.current[channel.id] = text; }, [text, channel.id]);
  useEffect(() => { if (editing) { setText(editing.content); setTimeout(() => { ta.current?.focus(); ta.current?.setSelectionRange(editing.content.length, editing.content.length); }); } }, [editing]);
  useEffect(() => { if (replyTo) ta.current?.focus(); }, [replyTo]);
  useLayoutEffect(() => { const el = ta.current; if (el) { el.style.height = "auto"; el.style.height = Math.min(el.scrollHeight, 200) + "px"; } }, [text]);
  useEffect(() => { if (!rec) return; const i = setInterval(() => setRecSecs(Math.floor((Date.now() - rec.start) / 1000)), 250); return () => clearInterval(i); }, [rec]);

  const addFiles = (list: FileList | File[]) => {
    if (!canAttach) return;
    for (const f of Array.from(list).slice(0, 10)) {
      const key = `${Date.now()}-${Math.random()}`;
      if (f.size > MAX_UPLOAD) { toast(t("fileTooLarge", { name: f.name, mb: 25 })); continue; }
      const u: Upload = { key, name: f.name || "file", type: f.type, progress: 0, preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : undefined };
      const job = upload(channel.id, f, u.name, (p) => setFiles((fs) => fs.map((x) => (x.key === key ? { ...x, progress: p } : x))));
      u.abort = job.abort;
      setFiles((fs) => [...fs, u]);
      job.promise.then((att) => setFiles((fs) => fs.map((x) => (x.key === key ? { ...x, att, progress: 1 } : x))),
        (e) => setFiles((fs) => fs.map((x) => (x.key === key ? { ...x, error: errText(e) } : x))));
    }
  };
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => { if (e.clipboardData?.files.length && document.activeElement === ta.current) { e.preventDefault(); addFiles(e.clipboardData.files); } };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  });

  const uploading = files.some((f) => !f.att && !f.error);
  const ready = files.filter((f) => f.att);
  const submit = async () => {
    const content = text.trim();
    if (editing) {
      if (!content) return;
      try { await patch(`/channels/${channel.id}/messages/${editing.id}`, { content }); setEditing(null); setText(""); } catch (e) { toast(errText(e)); }
      return;
    }
    if ((!content && !ready.length) || uploading) return;
    sendMessage(channel.id, content, { replyToId: replyTo?.id, attachmentIds: ready.map((f) => f.att!.id), attachments: ready.map((f) => ({ name: f.name, type: f.type })) });
    files.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
    setText(""); setFiles([]); setReplyTo(null); lastTyping.current = 0;
  };
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); void submit(); return; }
    if (e.key === "Escape") { if (editing) { setEditing(null); setText(""); } else setReplyTo(null); return; }
    if (e.key === "ArrowUp" && !text) {
      const me = getState().me!; const list = getState().messages[channel.id]?.list ?? [];
      const last = [...list].reverse().find((m) => m.author.id === me.id && m.content);
      if (last) { e.preventDefault(); setEditing(last); }
    }
  };
  const onChange = (v: string) => {
    setText(v);
    if (v && !editing && Date.now() - lastTyping.current > 3000) { lastTyping.current = Date.now(); post(`/channels/${channel.id}/typing`).catch(() => {}); }
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") { toast(t("micUnavailable")); return; }
    let stream: MediaStream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } }); }
    catch { toast(t("micDenied")); return; }
    const type = ["audio/webm;codecs=opus", "audio/ogg;codecs=opus", "audio/mp4"].find((x) => MediaRecorder.isTypeSupported(x)) ?? "";
    const mr = new MediaRecorder(stream, type ? { mimeType: type, audioBitsPerSecond: 64_000 } : undefined);
    const chunks: Blob[] = []; const start = Date.now(); let send = false;
    mr.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
    mr.onstop = () => {
      stream.getTracks().forEach((tr) => tr.stop());
      setRec(null); setRecSecs(0);
      if (!send || !chunks.length) return;
      const blob = new Blob(chunks, { type: mr.mimeType || "audio/webm" });
      const ms = Date.now() - start;
      const ext = blob.type.includes("ogg") ? "ogg" : blob.type.includes("mp4") ? "m4a" : "webm";
      const name = `voice-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.${ext}`;
      upload(channel.id, blob, name, () => {}, { "x-duration-ms": String(ms) }).promise
        .then((att) => sendMessage(channel.id, "", { attachmentIds: [att.id], attachments: [{ name, type: blob.type }], replyToId: replyTo?.id }))
        .catch((e) => toast(errText(e)));
      setReplyTo(null);
    };
    mr.start(250);
    const timer = setTimeout(() => { if (mr.state === "recording") { send = true; mr.stop(); } }, 5 * 60_000);
    setRec({ start, stop: (s) => { clearTimeout(timer); send = s; if (mr.state === "recording") mr.stop(); } });
  };

  if (!canSend) return <div className="composer"><div className="box disabled">{t("noSendPermission")}</div></div>;
  const hasContent = !!text.trim() || ready.length > 0;
  return (
    <div className="composer"
      onDragOver={(e) => { if (canAttach && e.dataTransfer.types.includes("Files")) { e.preventDefault(); e.currentTarget.classList.add("drag"); } }}
      onDragLeave={(e) => e.currentTarget.classList.remove("drag")}
      onDrop={(e) => { e.currentTarget.classList.remove("drag"); if (e.dataTransfer.files.length) { e.preventDefault(); addFiles(e.dataTransfer.files); } }}>
      <div className="box">
        {replyTo && <div className="bar"><Icon name="reply" size={16} /><span>{t("replyingTo", { name: displayName(replyTo.author) })}: <i dir="auto">{replyTo.content.slice(0, 80) || "📎"}</i></span><button className="ib sm" onClick={() => setReplyTo(null)} aria-label={t("cancel")}><Icon name="x" size={16} /></button></div>}
        {editing && <div className="bar"><Icon name="edit" size={16} /><span>{t("editing")}</span><button className="ib sm" onClick={() => { setEditing(null); setText(""); }} aria-label={t("cancel")}><Icon name="x" size={16} /></button></div>}
        {files.length > 0 && <div className="pend">{files.map((f) => (
          <div key={f.key} className={`pi${f.error ? " bad" : ""}`} title={f.error ?? f.name}>
            {f.preview ? <img src={f.preview} alt="" /> : <span className="fname" dir="auto"><Icon name="file" /><br />{f.name.slice(0, 24)}</span>}
            {!f.att && !f.error && <span className="prog"><span style={{ width: `${Math.round(f.progress * 100)}%` }} /></span>}
            {f.error && <span className="perr">{f.error}</span>}
            <button className="x" onClick={() => { f.abort?.(); if (f.preview) URL.revokeObjectURL(f.preview); setFiles((fs) => fs.filter((x) => x.key !== f.key)); }} aria-label={t("removeAttachment")}><Icon name="x" size={13} /></button>
          </div>))}</div>}
        {rec ? (
          <div className="rec" role="status"><span className="dotr" /><span className="mono">{Math.floor(recSecs / 60)}:{String(recSecs % 60).padStart(2, "0")}</span><span className="muted">{t("recording")}</span>
            <span className="sp" />
            <button className="ib" onClick={() => rec.stop(false)} aria-label={t("cancel")} title={t("cancel")}><Icon name="trash" /></button>
            <button className="send" onClick={() => rec.stop(true)} aria-label={t("stopAndSend")} title={t("stopAndSend")}><Icon name="send" className="flip" /></button></div>
        ) : (
          <div className="row-in">
            {canAttach && !editing && <><button className="ib" onClick={() => fileInput.current?.click()} aria-label={t("attach")} title={t("attach")}><Icon name="plus" /></button>
              <input ref={fileInput} type="file" multiple hidden onChange={(e) => { if (e.target.files) addFiles(e.target.files); e.target.value = ""; }} /></>}
            <textarea ref={ta} id="composer" rows={1} value={text} onChange={(e) => onChange(e.target.value)} onKeyDown={onKey} dir="auto"
              placeholder={channel.dm === "dm" ? t("messageDm", { name: channel.name }) : t("messagePlaceholder", { name: channel.name })} aria-label={t("messagePlaceholder", { name: channel.name })} maxLength={4000} />
            <div className="emoji-anchor">
              <button className="ib" onClick={() => setPicker((v) => !v)} aria-label={t("emoji")} title={t("emoji")}><Icon name="smile" /></button>
              {picker && <EmojiPicker onClose={() => setPicker(false)} onPick={(e) => {
                const el = ta.current; const pos = el?.selectionStart ?? text.length;
                setText(text.slice(0, pos) + e + text.slice(pos)); setPicker(false);
                setTimeout(() => { el?.focus(); el?.setSelectionRange(pos + e.length, pos + e.length); });
              }} />}
            </div>
            {hasContent || editing || !canAttach
              ? <button className="send" onClick={() => void submit()} disabled={uploading || (!hasContent && !editing)} aria-label={editing ? t("save") : t("send")} title={editing ? t("save") : t("send")}>{uploading ? <Spinner /> : <Icon name={editing ? "check" : "send"} className={editing ? "" : "flip"} />}</button>
              : <button className="send" onClick={() => void startRecording()} aria-label={t("recordVoice")} title={t("recordVoice")}><Icon name="mic" /></button>}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- typing line ----------
function Typing({ channelId, server }: { channelId: string; server: ServerState | null }) {
  const typing = useStore((s) => s.typing[channelId]);
  const users = useStore((s) => s.users);
  const names = Object.keys(typing ?? {}).map((id) => server?.members?.[id]?.nickname || displayName(users[id]));
  return <div className="typing" aria-live="polite">{names.length > 0 && <><i /><i /><i /> {names.length === 1 ? t("typingOne", { a: names[0]! }) : names.length === 2 ? t("typingTwo", { a: names[0]!, b: names[1]! }) : t("typingMany")}</>}</div>;
}

// ---------- pins & search panels ----------
export function PinsPanel({ channel, onClose }: { channel: ChatChannel; onClose: () => void }) {
  const [list, setList] = useState<Message[] | null>(null);
  useEffect(() => { get<Message[]>(`/channels/${channel.id}/pins`).then(setList, (e) => toast(errText(e))); }, [channel.id]);
  return (
    <aside className="side">
      <h3><Icon name="pin" size={18} /> {t("pinned")}<button className="ib sm" onClick={onClose} aria-label={t("close")}><Icon name="x" size={16} /></button></h3>
      {list === null ? <Spinner /> : list.length === 0 ? <p className="muted small">{t("noPins")}</p> :
        list.map((m) => <button key={m.id} className="pin" onClick={() => document.getElementById(`m-${m.id}`)?.scrollIntoView({ block: "center" })}>
          <b>{displayName(m.author)}</b><span dir="auto">{m.content || "📎"}</span></button>)}
    </aside>
  );
}

export function SearchPanel({ server, query, onClose, onJump }: { server: ServerState; query: string; onClose: () => void; onJump: (channelId: string, messageId: string) => void }) {
  const [res, setRes] = useState<{ total: number; messages: Message[] } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    setRes(null); setErr(null);
    const ac = new AbortController();
    const h = setTimeout(() => get(`/servers/${server.id}/messages/search?q=${encodeURIComponent(query)}`, { signal: ac.signal }).then(setRes, (e) => { if (e.name !== "AbortError") setErr(errText(e)); }), 250);
    return () => { clearTimeout(h); ac.abort(); };
  }, [query, server.id]);
  return (
    <aside className="side">
      <h3><Icon name="search" size={18} /> {res ? t("searchResults", { n: res.total }) : "…"}<button className="ib sm" onClick={onClose} aria-label={t("closeSearch")}><Icon name="x" size={16} /></button></h3>
      {err && <p className="err small">{err}</p>}
      {res && res.total === 0 && <p className="muted small">{t("noResults")}</p>}
      {res?.messages.map((m) => {
        const ch = server.channels.find((c) => c.id === m.channelId);
        return <button key={m.id} className="pin" onClick={() => onJump(m.channelId, m.id)}>
          <b>{displayName(m.author)} <span className="muted"># {ch?.name} · {dayLabel(m.createdAt)}</span></b><span dir="auto">{m.content || "📎"}</span></button>;
      })}
    </aside>
  );
}

// ---------- the chat view ----------
export function ChatView({ server, channel, jumpTo, onProfile }: { server: ServerState | null; channel: ChatChannel; jumpTo: string | null; onProfile: (userId: string) => void }) {
  const cmState = useStore((s) => s.messages[channel.id]);
  const pending = useStore((s) => s.pending[channel.id]) ?? [];
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const [editing, setEditing] = useState<Message | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [atBottom, setAtBottom] = useState(true);
  const feed = useRef<HTMLDivElement>(null);
  const prevHeight = useRef<number | null>(null);
  const stick = useRef(true);

  const epoch = useStore((s) => s.epoch);
  useEffect(() => { setReplyTo(null); setEditing(null); setErr(null); stick.current = true; }, [channel.id]);
  useEffect(() => { openChannel(channel.id).catch((e) => setErr(errText(e))); }, [channel.id, epoch]);
  const list = cmState?.list ?? [];
  const lastId = list.at(-1)?.id;
  useEffect(() => { setActiveChannel(channel.id); return () => setActiveChannel(null); }, [channel.id]);
  useEffect(() => {
    const tryAck = () => { if (lastId && stick.current && document.visibilityState === "visible" && document.hasFocus()) ack(channel.id, lastId); };
    tryAck();
    window.addEventListener("focus", tryAck); document.addEventListener("visibilitychange", tryAck);
    return () => { window.removeEventListener("focus", tryAck); document.removeEventListener("visibilitychange", tryAck); };
  }, [channel.id, lastId, atBottom]);

  useLayoutEffect(() => {
    const el = feed.current; if (!el) return;
    if (prevHeight.current !== null) { el.scrollTop += el.scrollHeight - prevHeight.current; prevHeight.current = null; return; }
    if (stick.current) el.scrollTop = el.scrollHeight;
  }, [list.length, pending.length, list.at(-1)?.id]);

  useEffect(() => {
    if (!jumpTo || !cmState?.loaded) return;
    const el = document.getElementById(`m-${jumpTo}`);
    if (el) { stick.current = false; el.scrollIntoView({ block: "center" }); el.classList.add("flash"); setTimeout(() => el.classList.remove("flash"), 1600); }
  }, [jumpTo, cmState?.loaded]);

  const onScroll = () => {
    const el = feed.current!;
    const bottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    stick.current = bottom; setAtBottom(bottom);
    if (el.scrollTop < 200 && cmState?.hasMore && !cmState.loading) {
      prevHeight.current = el.scrollHeight;
      loadOlder(channel.id).catch((e) => { prevHeight.current = null; toast(errText(e)); });
    }
  };

  let lastDay = "", prev: Message | null = null;
  return (
    <>
      <div className="feed" ref={feed} onScroll={onScroll} role="log" aria-live="polite">
        {err && <p className="err center">{err}</p>}
        {cmState?.loaded && !cmState.hasMore && <div className="start"><span className="big-hash"><Icon name={channel.dm ? (channel.dm === "dm" ? "at" : "users") : "hash"} size={34} /></span>
          <h2 dir="auto">{channel.dm === "dm" ? t("startConversation", { name: channel.name }) : channel.dm ? t("groupStart", { name: channel.name }) : t("beginning", { name: channel.name })}</h2>{channel.topic && <p className="muted" dir="auto">{channel.topic}</p>}</div>}
        {cmState?.loading && <div className="center muted small">{t("loadingOlder")}</div>}
        {list.map((m) => {
          const day = dayLabel(m.createdAt);
          const sep = day !== lastDay; lastDay = day;
          const grouped = !sep && !!prev && prev.author.id === m.author.id && !m.replyTo && Date.parse(m.createdAt) - Date.parse(prev.createdAt) < GROUP_MS;
          prev = m;
          return <Fragment key={m.id}>{sep && <div className="day"><span>{day}</span></div>}
            <MessageItem m={m} grouped={grouped} channel={channel} server={server} onReply={setReplyTo} onEdit={setEditing} onProfile={onProfile} /></Fragment>;
        })}
        {pending.map((p) => <PendingItem key={p.nonce} p={p} />)}
      </div>
      {!atBottom && <button className="jump" onClick={() => { const el = feed.current!; el.scrollTop = el.scrollHeight; }}>{t("jumpPresent")}</button>}
      <Typing channelId={channel.id} server={server} />
      <Composer channel={channel} replyTo={replyTo} setReplyTo={setReplyTo} editing={editing} setEditing={setEditing} />
    </>
  );
}

export { ApiError, Modal };
